import { useCallback, useEffect, useRef, useState } from "react";
import Button from "@mui/material/Button";
import styles from "./transactionEditModal.module.scss";
// import Autocomplete from "@mui/material/Autocomplete";
// import TextField from "@mui/material/TextField";
import { Avatar, CircularProgress } from "@mui/material";
import {
  Transaction,
  createNoteAction,
  deleteFile,
  getNoteAction,
  searchMccAction,
  updateGstAmountAction
} from "src/page/transactions/transactionSlice";
import { useAppDispatch, useAppSelector } from "src/page/store";
import { bgColorbyName, typeColors } from "src/variable/randomColor";
import {
  dateTimeFormat,
  getDecimalNumber,
  getSignedUrlFromS3,
  getTransactionValue,
  isUUID,
  processFileName,
  uploadFileToS3
} from "src/utils/utils";
import { transactionPhoto } from "src/network/graphql/transactionService";
import PhotoCard, { AssetUrl } from "src/components/photoCard/PhotoCard";
import { ListAsset } from "src/types/commonTypes";
import TextSnippetOutlinedIcon from "@mui/icons-material/TextSnippetOutlined";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import { getConfig } from "src/config/Config";
import { useDropzone, FileRejection } from "react-dropzone";
import { immediateToast } from "izitoast-react";
import { activityNameMapping, EMPTY_STRING, walletTypeMapping } from "src/components/constant/constant";
import { getUserById } from "src/network/graphql/userService";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import { Link } from "react-router-dom";

interface Props {
  transaction: Transaction;
  handleClose: () => void;
}

export default function TransactionEditModal({ transaction, handleClose }: Props) {
  const dispatch = useAppDispatch();
  const { noteLoading, notes, mccList } = useAppSelector((state) => state.transaction);
  const { userInfo } = useAppSelector((state) => state.auth);

  const [note, setNote] = useState<string>(EMPTY_STRING);
  const [gst, setGst] = useState<string>(EMPTY_STRING);
  const [photos, setPhotos] = useState<AssetUrl[]>([]);
  const [receipts, setReceipts] = useState<AssetUrl[]>([]);
  const [photosUpdated, setPhotosUpdated] = useState(false);
  const [visibleNotes, setVisibleNotes] = useState(2);
  const [noteError, setNoteError] = useState<string>(EMPTY_STRING);
  const hiddenAddReceipt = useRef<HTMLInputElement>(null);
  const hiddenAddPhoto = useRef<HTMLInputElement>(null);
  const [userNamesCache, setUserNamesCache] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    dispatch(getNoteAction(transaction?.id));
    setGst(String(transaction?.data?.price_components?.gst));
  }, [dispatch, transaction?.id]);

  const handleSeeMore = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setVisibleNotes((prev) => prev + 2);
  };

  const getUserName = async (userId: string) => {
    if (userInfo?.id === userId) {
      return `You`;
    } else {
      try {
        const response = await getUserById({ user_id: userId });
        const user = response.data.get_user;
        const username = `${user?.attributes.initial} ${user.attributes.first_name} ${user.attributes.last_name}`;

        return username;
      } catch (err) {
        console.log(err);
        return userId;
      }
    }
  };

  const fetchUserName = async (userId: string) => {
    if (userNamesCache[userId]) return userNamesCache[userId];

    if (!isUUID(userId)) {
      setUserNamesCache((prev) => ({ ...prev, [userId]: userId }));
      return userId;
    }

    const name = await getUserName(userId);

    setUserNamesCache((prev) => ({ ...prev, [userId]: name }));
    return name;
  };

  useEffect(() => {
    if (transaction?.data?.merchantCategoryCode) {
      dispatch(
        searchMccAction({
          filter: {
            mcc_filter: {
              mcc: [transaction?.data?.merchantCategoryCode]
            }
          }
        })
      );
    }
  }, [dispatch, transaction?.data?.merchantCategoryCode]);

  const handleSaveNotes = () => {
    const trimmedNote = note.trim();

    if (!trimmedNote) {
      setNoteError("Note cannot be empty");
      return;
    }

    dispatch(
      createNoteAction(transaction?.id, note, () => {
        setNote(EMPTY_STRING);
        setNoteError(EMPTY_STRING);
        dispatch(getNoteAction(transaction?.id));
      })
    );

    const parsedGst = parseFloat(gst);

    if (!isNaN(parsedGst) && transaction?.data?.price_components?.gst !== parsedGst) {
      dispatch(updateGstAmountAction(transaction?.id, parsedGst));
    }
  };

  useEffect(() => {
    const fetchAssets = async () => {
      try {
        const photoResponse = await transactionPhoto("PHOTO", transaction?.id);
        const receiptResponse = await transactionPhoto("RECEIPT", transaction?.id);

        const photoAssets: ListAsset[] = photoResponse.data.list_assets.asset.filter(
          (asset: ListAsset) => asset.asset_status === "ACTIVE"
        );
        const receiptAssets: ListAsset[] = receiptResponse.data.list_assets.asset.filter(
          (asset: ListAsset) => asset.asset_status === "ACTIVE"
        );

        const photoUrls = await Promise.all(
          photoAssets.map((photo: ListAsset) => getSignedUrlFromS3(photo.attributes.bucket_name, photo.attributes.key))
        );

        const receiptUrls = await Promise.all(
          receiptAssets.map((receipt: ListAsset) =>
            getSignedUrlFromS3(receipt.attributes.bucket_name, receipt.attributes.key)
          )
        );

        setPhotos(
          photoAssets.map((photo: ListAsset, index: number) => ({
            id: photo.attributes.key,
            url: photoUrls[index],
            assetId: photo.asset_id
          }))
        );
        setReceipts(
          receiptAssets.map((receipt: ListAsset, index: number) => ({
            id: receipt.attributes.key,
            url: receiptUrls[index],
            assetId: receipt.asset_id
          }))
        );
      } catch (error) {
        console.error("Error fetching assets:", error);
      }

      setPhotosUpdated(false);
    };

    fetchAssets();
  }, [photosUpdated, transaction?.id]);

  const handleFileUpload = async (file: File, type: "PHOTO" | "RECEIPT") => {
    const data = {
      file: file,
      fileName: processFileName(file.name, transaction?.id),
      S3Bucket: getConfig()?.S3_ASSET_BUCKET,
      MetaData: {
        object_id: transaction?.id,
        object_type: "TRANSACTION",
        asset_type: type
      }
    };

    await uploadFileToS3(data);
    setPhotosUpdated(true);
  };

  const handlePhotoDelete = async (assetId: string) => {
    dispatch(
      deleteFile(assetId, () => {
        setPhotosUpdated(true);
      })
    );
  };

  const onDropReceipt = useCallback((acceptedFiles: File[], fileRejections: FileRejection[]) => {
    if (fileRejections.length > 0) {
      immediateToast("error", {
        message: `The format is not supported for receipts.`,
        timeout: 3000,
        position: "topCenter"
      });
      return;
    }
    acceptedFiles.forEach((file: File) => {
      handleFileUpload(file, "RECEIPT");
    });
  }, []);

  const onDropPhoto = useCallback((acceptedFiles: File[], fileRejections: FileRejection[]) => {
    if (fileRejections.length > 0) {
      immediateToast("error", {
        message: `The format is not supported for photos.`,
        timeout: 3000,
        position: "topCenter"
      });
      return;
    }
    acceptedFiles.forEach((file: File) => {
      handleFileUpload(file, "PHOTO");
    });
  }, []);

  const { getRootProps: getRootPropsReceipt, isDragActive: isDragActiveReceipt } = useDropzone({
    onDrop: onDropReceipt,
    noClick: true,
    accept: {
      "image/*": [],
      "application/pdf": [".pdf"]
    }
  });

  const { getRootProps: getRootPropsPhoto, isDragActive: isDragActivePhoto } = useDropzone({
    onDrop: onDropPhoto,
    noClick: true,
    accept: {
      "image/*": [],
      "application/pdf": [".pdf"]
    }
  });

  const cutOffDate = "2024-03-13T00:00:00Z";
  const rednerBalance = () => {
    if (transaction?.endDate > cutOffDate) {
      if (transaction?.data?.wallet?.balance) {
        if (transaction?.data?.wallet?.balance > 0) {
          return "$" + getDecimalNumber(transaction?.data?.wallet?.balance);
        } else {
          return "-$" + getDecimalNumber(transaction?.data?.wallet?.balance);
        }
      } else {
        return "$0.00";
      }
    } else {
      return "---";
    }
  };

  useEffect(() => {
    const fetchUserNames = async () => {
      const notesToFetch = notes.slice(0, visibleNotes);

      for (const note of notesToFetch) {
        if (note.created_by && !userNamesCache[note.created_by]) {
          await fetchUserName(note.created_by);
        }
      }
    };

    fetchUserNames();
  }, [notes, visibleNotes]);

  return (
    <div className={styles.referModalBox}>
      <header className={styles.referModalTop}>
        <div className={styles.topL}>
          <p>
            Transaction Id: {transaction?.id}
            <br />
            <span>{dateTimeFormat(transaction?.endDate)?.datetime}</span>
          </p>
        </div>
        <div className={styles.topR}>
          <Button className={styles.closeBtn} variant="contained" onClick={handleClose} aria-label="Close">
            Close
          </Button>
        </div>
      </header>
      {noteLoading ? (
        <div className={styles.progress}>
          <CircularProgress sx={{ color: "#0f172a" }} />
        </div>
      ) : (
        <>
          <main className={styles.referModalBottom} id={styles.scrollBar}>
            <form id={styles.scrollBarH}>
              <div className={styles.gridContainer}>
                <div className={`${styles.gridItem} ${styles.amount}`}>
                  <span className={styles.title}>Amount</span>
                  <span className={styles.input} style={{ color: getTransactionValue(transaction)?.amountColor }}>
                    {getTransactionValue(transaction)?.amount}
                  </span>
                </div>

                <div className={styles.gridItem}>
                  <span className={styles.title}>Merchant</span>
                  <span className={styles.input}>
                    {`${getTransactionValue(transaction)?.value ?? "---"} (${transaction?.data?.merchantId ?? "---"})`}
                  </span>
                </div>

                <div className={styles.gridItem}>
                  <span className={styles.title}>Merchant Category</span>
                  <span className={styles.input}>
                    {mccList?.list[0]
                      ? `${mccList.list[0]?.mcc_category} (${mccList.list[0]?.mcc})`
                      : transaction?.data?.merchantCategoryCode || "---"}
                  </span>
                </div>

                <div className={`${styles.gridItem} ${styles.hasAvatar}`}>
                  <span className={styles.title}>Wallet</span>
                  <div className={`${styles.input}`}>
                    <div className={styles.inputL}>
                      <Avatar
                        id="av"
                        aria-label={`Wallet ${transaction?.data?.wallet?.name}`}
                        style={{ backgroundColor: bgColorbyName(transaction?.data?.wallet?.name) }}
                      >
                        {transaction?.data?.wallet?.name?.charAt(0).toLocaleUpperCase()}
                      </Avatar>
                    </div>
                    <div className={styles.inputR}>
                      <span className={styles.name}>
                        {transaction?.data?.wallet?.name} |
                        <div className={styles.type}>
                          <div
                            className={styles.typeColor}
                            style={{
                              backgroundColor: `${typeColors[transaction?.data?.wallet?.wallet_type]}0d`,
                              borderColor: `${typeColors[transaction?.data?.wallet?.wallet_type]}73`,
                              color: `${typeColors[transaction?.data?.wallet?.wallet_type]}FF`
                            }}
                          >
                            {walletTypeMapping[transaction?.data?.wallet?.wallet_type]}
                          </div>
                        </div>
                      </span>
                      <Link
                        to={
                          transaction?.data?.wallet?.object_type === "GROUP"
                            ? `/groups/${transaction?.ownerId}`
                            : `/users/${transaction?.ownerId}`
                        }
                        className={styles.walletName}
                      >
                        View Wallet Owner <ArrowOutwardIcon fontSize="small" />
                      </Link>
                    </div>
                  </div>
                </div>

                <div className={styles.gridItem}>
                  <span className={styles.title}>Balance</span>
                  <span
                    className={styles.input}
                    style={{
                      color:
                        transaction?.endDate > cutOffDate && transaction?.data?.wallet?.balance > 0 ? "green" : "red"
                    }}
                  >
                    {rednerBalance()}
                  </span>
                </div>

                <div className={`${styles.gridItem} ${styles.hasAvatar}`}>
                  <span className={styles.title}>Purchase made by</span>
                  <div className={styles.input}>
                    <div className={styles.inputL}>
                      <Avatar
                        id="av"
                        aria-label="Recipe"
                        style={{
                          backgroundColor: bgColorbyName(
                            transaction?.data?.transaction_user
                              ? transaction?.data?.transaction_user?.attributes?.first_name
                              : transaction?.data?.user?.attributes?.first_name
                          )
                        }}
                      >
                        {transaction?.data?.transaction_user
                          ? transaction?.data?.transaction_user?.attributes?.first_name?.charAt(0).toLocaleUpperCase()
                          : transaction?.data?.user?.attributes?.first_name?.charAt(0).toLocaleUpperCase()}
                      </Avatar>
                    </div>
                    <div className={styles.inputR}>
                      <span className={styles.name}>
                        {/* eslint-disable-next-line no-nested-ternary */}
                        {transaction?.data?.transaction_user
                          ? transaction?.data?.transaction_user?.attributes?.first_name +
                            " " +
                            transaction?.data?.transaction_user?.attributes?.last_name
                          : transaction?.data?.user
                            ? transaction?.data?.user?.attributes?.first_name +
                              " " +
                              transaction?.data?.user?.attributes?.last_name
                            : "---"}
                      </span>
                      <span className={styles.email} style={{ color: "#64748B" }}>
                        {/* eslint-disable-next-line no-nested-ternary */}
                        {transaction?.data?.transaction_user
                          ? transaction?.data?.transaction_user?.email
                          : transaction?.data?.user
                            ? transaction?.data?.user?.email
                            : "---"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className={styles.gridItem}>
                  <span className={styles.title}>Activity Name</span>
                  <span className={styles.input}>{activityNameMapping[transaction?.name]}</span>
                </div>

                <div className={`${styles.gridItem} ${styles.gst}`}>
                  <span className={styles.title}>GST Amount</span>
                  <input
                    type="number"
                    placeholder="Enter GST amount"
                    value={gst}
                    onChange={(e) => setGst(e.target.value)}
                  />
                </div>

                {transaction?.name === "NOVATTI_CARD_TRANSACTION" && (
                  <div className={styles.gridItem}>
                    <span className={styles.title}>Card Used</span>
                    <span className={styles.input}>{transaction?.data?.card?.holder_name}</span>
                    <span className={styles.input} style={{ color: "#64748B" }}>
                      {transaction?.data?.ppan ? "************" + transaction?.data?.ppan.slice(-4) : "----"}
                    </span>
                  </div>
                )}
              </div>
              <div className={styles.dropNdrag}>
                <div className={styles.bottomIV}>
                  <div className={styles.receipt}>
                    <div className={styles.title}>Receipt</div>
                    <Button
                      variant="contained"
                      className={styles.addReceipt}
                      onClick={() => {
                        if (hiddenAddReceipt.current) {
                          hiddenAddReceipt.current.click();
                        }
                      }}
                    >
                      <input
                        id="hiddenAddReceiptInput"
                        type="file"
                        accept="image/*,application/pdf"
                        onChange={(e) => {
                          if (e.target.files && e.target.files[0]) {
                            handleFileUpload(e.target.files[0], "RECEIPT");
                          }
                        }}
                        ref={hiddenAddReceipt}
                        style={{ display: "none" }}
                        aria-label="Add Receipt"
                      />
                      <div className={styles.logo}>
                        <TextSnippetOutlinedIcon />
                      </div>
                      <div className={styles.text}>Add Receipt</div>
                    </Button>
                    <div className={styles.filepondWrapper} {...getRootPropsReceipt()}>
                      {/* eslint-disable-next-line no-nested-ternary */}
                      {isDragActiveReceipt || receipts.length === 0 ? (
                        <div className={styles.noData}>
                          Drag & Drop
                          <br />
                          Add receipt
                        </div>
                      ) : (
                        receipts.map((receipt) => (
                          <PhotoCard key={receipt.id} data={receipt} onDelete={handlePhotoDelete} />
                        ))
                      )}
                    </div>
                  </div>
                </div>
                <div className={styles.bottomV}>
                  <div className={styles.photo}>
                    <div className={styles.title}>Photos</div>
                    <Button
                      variant="contained"
                      className={styles.addPhoto}
                      onClick={() => {
                        if (hiddenAddPhoto.current) {
                          hiddenAddPhoto.current.click();
                        }
                      }}
                    >
                      <div className={styles.logo}>
                        <CameraAltOutlinedIcon />
                      </div>
                      <div className={styles.text}>Add Photo</div>
                    </Button>
                    <input
                      id="hiddenAddPhotoInput"
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          handleFileUpload(e.target.files[0], "PHOTO");
                        }
                      }}
                      ref={hiddenAddPhoto}
                      aria-label="Add Photo"
                      style={{ display: "none" }}
                    />
                    <div className={styles.filepondWrapper} {...getRootPropsPhoto()}>
                      {/* eslint-disable-next-line no-nested-ternary */}
                      {isDragActivePhoto || photos.length === 0 ? (
                        <div className={styles.noData}>
                          Drag & Drop
                          <br />
                          Add photo
                        </div>
                      ) : (
                        photos.map((photo) => <PhotoCard key={photo.id} data={photo} onDelete={handlePhotoDelete} />)
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className={styles.bottomVI}>
                <span className={styles.title}>Notes</span>
                <textarea
                  className={styles.input}
                  placeholder="Type your comments..."
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                ></textarea>
                {noteError && (
                  <div className={styles.error}>
                    <ErrorOutlineIcon className={styles.errIcon} />
                    {noteError}
                  </div>
                )}
              </div>
              <div className={styles.noteHistory}>
                {notes && notes.length > 0 && (
                  <>
                    <span className={styles.title}>Note History</span>
                    {notes.slice(0, visibleNotes).map((note, index) => (
                      <div key={index} className={styles.noteContainer}>
                        <p>{note.message}</p>
                        <div className={styles.noteBottom}>
                          <span className={styles.item}>
                            <span className={styles.itemTitle}>updated:</span>
                            {dateTimeFormat(note.updated_at)?.datetime}
                          </span>
                          {!!note?.created_by && (
                            <span className={styles.item}>
                              <span className={styles.itemTitle}>created By:</span>
                              {userNamesCache[note.created_by] || "Loading..."}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}

                    {visibleNotes < notes.length && (
                      <div className={styles.seeMoreContainer}>
                        <Button variant="contained" className={styles.seeMoreButton} onClick={handleSeeMore}>
                          See More
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </form>
          </main>

          <footer className={styles.footer}>
            <div className={styles.saveBtn}>
              <Button variant="contained" className={styles.requestBtn} onClick={handleSaveNotes}>
                Save
              </Button>
            </div>
          </footer>
        </>
      )}
    </div>
  );
}
